import { useState } from "react";
import {
  QuestionBankItem,
  QuestionBankItemStatus,
  QuestionCategory,
} from "../types/question-bank.types";
import CategoryDropdown from "./CategoryDropdown";
import DifficultyDropdown from "./DifficultyDropdown";

interface QuestionBankItemHeaderProps {
  item: QuestionBankItem;
  onChangeStatus: (isPublished: boolean) => void;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onBack: () => void;
  canUndo: boolean;
  canRedo: boolean;
  isSaving: boolean;
  isDirty: boolean;
  categories: QuestionCategory[];
  onCategoryChange: (categoryId: string) => void;
  onDifficultyChange: (difficulty: "easy" | "medium" | "hard") => void;
}

/**
 * Header component for the QuestionBankItemEditor
 */
export default function QuestionBankItemHeader({
  item,
  onChangeStatus,
  onSave,
  onUndo,
  onRedo,
  onBack,
  canUndo,
  canRedo,
  isSaving,
  isDirty,
  categories,
  onCategoryChange,
  onDifficultyChange,
}: QuestionBankItemHeaderProps) {
  const isPublished = item.status === QuestionBankItemStatus.PUBLISHED;
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);

  return (
    <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex flex-col sm:flex-row justify-between items-start sm:items-center shadow-md w-full">
      <div className="flex flex-col gap-2 w-full">
        {/* Main fixed container */}
        <div className="flex items-center mb-4 sm:mb-0 w-full">
          <button
            onClick={onBack}
            className="mr-3 text-gray-500 hover:text-gray-700"
            aria-label="Back"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              ></path>
            </svg>
          </button>

          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
            {item.header?.title || "Untitled Question"}
          </h1>
        </div>

        <div className="flex items-center justify-between space-x-2">
          {/* Category and Difficulty Dropdowns */}
          <div className="flex flex-1 items-center space-x-4">
            {/* Category Dropdown */}
            <div className="flex flex-row items-center gap-2">
              <div className="text-gray-400 dark:text-gray-500 italic mb-2">
                Category
              </div>
              <CategoryDropdown
                categories={categories}
                selectedCategoryId={item.categoryId || null}
                onChange={onCategoryChange}
                placeholder="Select Category"
              />
            </div>

            {/* Difficulty Dropdown */}
            <div className="flex flex-row items-center gap-2">
              <div className="text-gray-400 dark:text-gray-500 italic mb-2">
                Difficulty
              </div>
              <DifficultyDropdown
                selectedDifficulty={item.difficulty || "medium"}
                onChange={onDifficultyChange}
                placeholder="Select Difficulty"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Published Status Switch */}
            <div className="flex items-center mr-2">
              <div className="text-sm text-gray-400 dark:text-gray-500 italic mr-2">
                {isPublished ? "Published" : "Draft"}
              </div>
              <div className="form-switch">
                <input
                  type="checkbox"
                  id="switch-1"
                  className="sr-only"
                  checked={isPublished}
                  onChange={() => onChangeStatus(!isPublished)}
                />
                <label
                  className="bg-gray-400 dark:bg-gray-700"
                  htmlFor="switch-1"
                >
                  <span
                    className="bg-white shadow-sm"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Published status</span>
                </label>
              </div>
            </div>

            {/* Undo button */}
            <button
              onClick={onUndo}
              disabled={!canUndo}
              className={`px-3 py-2 rounded-md flex items-center ${
                canUndo
                  ? "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                  : "text-gray-400 dark:text-gray-600 cursor-not-allowed"
              }`}
              title="Undo"
            >
              <svg
                className="w-5 h-5 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                ></path>
              </svg>
              Undo
            </button>

            {/* Redo button */}
            <button
              onClick={onRedo}
              disabled={!canRedo}
              className={`px-3 py-2 rounded-md flex items-center ${
                canRedo
                  ? "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                  : "text-gray-400 dark:text-gray-600 cursor-not-allowed"
              }`}
              title="Redo"
            >
              <svg
                className="w-5 h-5 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M14 10h-4a8 8 0 00-8 8v2M14 10l-6 6m6-6l-6-6"
                ></path>
              </svg>
              Redo
            </button>

            {/* Save button */}
            <button
              onClick={onSave}
              disabled={isSaving || !isDirty}
              className={`px-4 py-2 rounded-md flex items-center ${
                isSaving || !isDirty
                  ? "bg-indigo-400 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              } text-white`}
            >
              {isSaving ? <>Saving...</> : <>Save</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
